import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rtlLanguages, changeLanguage } from '../i18n/config';

// Normalize string for permissive search
const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s]/g, ''); // Remove special characters
};

const languages = [
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ca', name: 'Català', flag: '🇪🇸' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'id', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', name: '中文', flag: '🇨🇳' }
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = rtlLanguages.includes(i18n.language);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsMobileModalOpen(false);
        setSearchQuery('');
      }
    };

    const handleResize = () => {
      // Close any open UI when switching between mobile/desktop
      setIsOpen(false);
      setIsMobileModalOpen(false);
      setSearchQuery('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobile = () => window.innerWidth < 768; // md breakpoint

  const handleLanguageChange = async (newLang) => {
    const currentLang = i18n.language;
    
    setIsOpen(false);
    setIsMobileModalOpen(false);
    setSearchQuery('');
    
    if (currentLang === newLang) return;

    // Save language preference
    localStorage.setItem('tripreel-language', newLang);
    
    // Change language (lazy loads the translation file if needed)
    await changeLanguage(newLang);

    // Handle URL navigation using React Router
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    
    // Remove any existing language prefix
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
    
    if (newLang === 'en') {
      // English is the default - no prefix
      navigate(pathWithoutLang === '' ? '/' : pathWithoutLang + currentSearch + currentHash);
    } else {
      // Add language prefix for other languages
      if (pathWithoutLang === '/') {
        navigate(`/${newLang}` + currentSearch + currentHash);
      } else {
        navigate(`/${newLang}${pathWithoutLang}` + currentSearch + currentHash);
      }
    }

    // Track language change
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'language_change', {
        event_category: 'user_preference',
        event_label: newLang
      });
    }
  };

  const handleButtonClick = () => {
    if (isMobile()) {
      setIsMobileModalOpen(!isMobileModalOpen);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const filteredLanguages = languages
    .filter(lang => {
      const normalizedQuery = normalizeString(searchQuery);
      const normalizedName = normalizeString(lang.name);
      const normalizedCode = normalizeString(lang.code);
      return normalizedName.includes(normalizedQuery) || normalizedCode.includes(normalizedQuery);
    })
    .sort((a, b) => {
      // Current language first
      if (a.code === i18n.language) return -1;
      if (b.code === i18n.language) return 1;
      
      // English second
      if (a.code === 'en') return -1;
      if (b.code === 'en') return 1;
      
      // Then alphabetical by name
      return a.name.localeCompare(b.name);
    });

  return (
    <>
      {/* Mobile Modal - Rendered First */}
      {isMobileModalOpen && isMobile() && (
        <div
          ref={modalRef}
          className="fixed z-[100] bg-white dark:bg-slate-800 rounded-t-2xl shadow-2xl flex flex-col"
          style={{
            position: 'fixed',
            left: '50%',
            top: '4rem', // Below navbar (h-16 = 4rem)
            width: '100vw',
            maxWidth: '100vw',
            height: 'calc(100vh - 4rem)', // Full height minus navbar
            transform: 'translateX(-50%)'
          }}
        >
            {/* Search and Close */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                  <input
                    type="text"
                    placeholder=""
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full py-3 text-base bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </div>
                <button
                  onClick={() => {
                    setIsMobileModalOpen(false);
                    setSearchQuery('');
                  }}
                  className="p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
              </div>
            </div>

            {/* Language List */}
            <div className="flex-1 overflow-y-auto">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLanguageChange(lang.code);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLanguageChange(lang.code);
                    }}
                    className={`w-full flex items-center gap-4 px-4 py-4 active:bg-slate-100 dark:active:bg-slate-600 transition-colors duration-200 ${
                      i18n.language === lang.code ? 'bg-sky-50 dark:bg-sky-900/20' : ''
                    }`}
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="text-base font-medium text-slate-900 dark:text-slate-100">
                        {lang.name}
                      </span>
                      <span className="block text-sm text-slate-500 dark:text-slate-400 uppercase">
                        {lang.code}
                      </span>
                    </div>
                    {i18n.language === lang.code && (
                      <span className="w-3 h-3 bg-sky-600 dark:bg-sky-400 rounded-full"></span>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
-                </div>
              )}
            </div>
          </div>
      )}

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleButtonClick}
          className={`flex items-center justify-center gap-1 pt-2.5 pb-2.5 h-10 rounded-lg bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 shadow-md hover:shadow-lg ${isRTL ? 'pr-2.5 pl-1' : 'pl-2.5 pr-1'}`}
          aria-label="Change language"
        >
          <span className="text-lg">{currentLanguage.flag}</span>
          <ChevronDown className={`w-2.5 h-2.5 text-slate-600 dark:text-slate-400 transition-transform duration-200 ${isRTL ? 'ml-1' : 'mr-1'} ${isOpen || isMobileModalOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Desktop Dropdown - Hidden on mobile */}
        {isOpen && (
          <div className={`hidden md:block absolute mt-2 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto ${isRTL ? 'left-0' : 'right-0'}`}>
            <div className="p-3 border-b border-slate-200 dark:border-slate-700">
              <div className="relative">
                <Search className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input
                  type="text"
                  placeholder=""
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full py-2 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'}`}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLanguageChange(lang.code);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200 ${
                      i18n.language === lang.code ? 'bg-sky-50 dark:bg-sky-900/20' : ''
                    }`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {lang.name}
                    </span>
                    {i18n.language === lang.code && (
                      <span className={`${isRTL ? 'mr-auto' : 'ml-auto'} w-2 h-2 bg-sky-600 dark:bg-sky-400 rounded-full`}></span>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                  -
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
